import {
  Document,
  Font,
  Image,
  Page,
  StyleSheet,
  Text,
  View,
} from '@react-pdf/renderer'

import { getPdfBadgeColors } from '@/components/organisms/Viewer/pdfTheme'
import { picturesFormater } from '@/services/adapters'
import type { AdaptedProduct, Context } from '@/types'

import {
  buildOperationalResourceSheets,
  type OperationalResourceSheet,
  type OperationalResourceSheetItem,
} from './operationalReportingDocuments'

type OperationalResourceSheetsDocumentProps = {
  products: AdaptedProduct[]
  context: Context
  date?: string
  locale?: string
}

const SKOP_HEADER_COLOR = '#173f3d'

const getOperationalReportingFontSrc = (fileName: string) => {
  if (
    typeof process !== 'undefined' &&
    process.env.NODE_ENV === 'test' &&
    typeof process.cwd === 'function'
  ) {
    return `${process.cwd()}/public/fonts/${fileName}`
  }

  return `/fonts/${fileName}`
}

Font.register({
  family: 'InterOperationalReporting',
  fonts: [
    {
      src: getOperationalReportingFontSrc('Inter_18pt-Regular.ttf'),
      fontWeight: 'normal',
      fontStyle: 'normal',
    },
    {
      src: getOperationalReportingFontSrc('Inter_18pt-SemiBold.ttf'),
      fontWeight: 'bold',
      fontStyle: 'normal',
    },
  ],
})

const DATE_ONLY_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/

const parseDateLabel = (value: string): Date | null => {
  const dateOnlyMatch = DATE_ONLY_PATTERN.exec(value)
  if (dateOnlyMatch) {
    const [, yearValue, monthValue, dayValue] = dateOnlyMatch
    const year = Number(yearValue)
    const month = Number(monthValue)
    const day = Number(dayValue)
    const parsed = new Date(year, month - 1, day)
    if (
      parsed.getFullYear() === year &&
      parsed.getMonth() === month - 1 &&
      parsed.getDate() === day
    ) {
      return parsed
    }
    return null
  }

  const parsed = new Date(value)
  return Number.isNaN(parsed.getTime()) ? null : parsed
}

const formatDateLabel = (value?: string, locale = 'fr-FR'): string => {
  if (!value) return ''
  const parsed = parseDateLabel(value)
  if (!parsed) return value

  return parsed.toLocaleDateString(locale)
}

const formatItemValue = (item: OperationalResourceSheetItem): string =>
  `${item.value}${item.unit ? ` ${item.unit}` : ''}`

const getDocumentLabel = (
  products: AdaptedProduct[],
  context: Context,
): string => {
  const productProjectLabel = products.find(
    (product) => typeof product.projectID?.label === 'string',
  )?.projectID?.label
  if (productProjectLabel) return productProjectLabel

  const settingsName = context.customSettings?.name
  return typeof settingsName === 'string' ? settingsName : ''
}

export const resolveOperationalResourceSheetsHeaderColors = (
  context: Context,
) =>
  getPdfBadgeColors(
    context.organizationScope?.selectedOrganization?.color ??
      context.team?.color ??
      context.customSettings?.color ??
      SKOP_HEADER_COLOR,
  )

const Section = ({
  title,
  items,
}: {
  title: string
  items: OperationalResourceSheetItem[]
}) => {
  if (!items.length) return null

  return (
    <View style={styles.section}>
      <Text style={styles.sectionTitle}>{title}</Text>
      {items.map((item) => (
        <View style={styles.tableRow} key={item.key}>
          <Text style={styles.tableLabel}>{item.label}</Text>
          <Text style={styles.tableValue}>{formatItemValue(item)}</Text>
        </View>
      ))}
    </View>
  )
}

const PictureColumn = ({ sheet }: { sheet: OperationalResourceSheet }) => {
  const pictures = picturesFormater(sheet.pictures, 'original').slice(0, 2)

  return (
    <View style={styles.pictureColumn}>
      {pictures.length ? (
        pictures.map((picture) => (
          <Image
            key={`${sheet.id}-${picture}`}
            src={picture}
            style={styles.picture}
          />
        ))
      ) : (
        <View style={styles.picturePlaceholder}>
          <Text style={styles.picturePlaceholderText}>Aucune photo</Text>
        </View>
      )}
    </View>
  )
}

const ResourceSheetPage = ({
  sheet,
  headerColors,
  date,
  locale,
}: {
  sheet: OperationalResourceSheet
  headerColors: ReturnType<typeof resolveOperationalResourceSheetsHeaderColors>
  date?: string
  locale?: string
}) => (
  <Page size="A4" orientation="portrait" style={styles.page}>
    <View
      style={[
        styles.header,
        {
          backgroundColor: headerColors.backgroundColor,
          color: headerColors.color,
        },
      ]}
    >
      <View
        style={[
          styles.headerCell,
          { borderRight: `0.75 solid ${headerColors.color}` },
        ]}
      >
        <Text style={[styles.headerKicker, { color: headerColors.color }]}>
          CARNET FICHES RESSOURCES
        </Text>
      </View>
      <View style={styles.headerCenter}>
        <Text style={[styles.headerTitle, { color: headerColors.color }]}>
          {sheet.title}
        </Text>
        <Text style={[styles.headerSubtitle, { color: headerColors.color }]}>
          {sheet.categoryLabel || 'Ressource'}
        </Text>
      </View>
    </View>

    <View style={styles.body}>
      <PictureColumn sheet={sheet} />
      <View style={styles.contentColumn}>
        <Section title="Caractéristiques" items={sheet.characteristics} />
        <Section title="Dimensions et quantités" items={sheet.dimensions} />
        <Section
          title="Dépose et préparation au réemploi / recyclage"
          items={sheet.preparation}
        />
        <Section
          title="Indicateurs économie circulaire"
          items={sheet.circularEconomyIndicators}
        />
        <Section title="Pistes de réemploi" items={sheet.reuseTracks} />

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Débouché cible</Text>
          <View style={styles.tableRow}>
            <Text style={styles.tableLabel}>Scénario</Text>
            <Text style={styles.tableValue}>
              {sheet.targetOutlet.label || 'Non renseigné'}
            </Text>
          </View>
        </View>
      </View>
    </View>

    <View style={styles.footer}>
      <Text style={styles.footerText}>Reporting opérationnel chantier</Text>
      {date ? (
        <Text style={styles.footerText}>
          Version du {formatDateLabel(date, locale)}
        </Text>
      ) : null}
    </View>
  </Page>
)

const EmptyResourceSheetsPage = ({
  headerColors,
  date,
  locale,
}: {
  headerColors: ReturnType<typeof resolveOperationalResourceSheetsHeaderColors>
  date?: string
  locale?: string
}) => (
  <Page size="A4" orientation="portrait" style={styles.page}>
    <View
      style={[
        styles.header,
        {
          backgroundColor: headerColors.backgroundColor,
          color: headerColors.color,
        },
      ]}
    >
      <View
        style={[
          styles.headerCell,
          { borderRight: `0.75 solid ${headerColors.color}` },
        ]}
      >
        <Text style={[styles.headerKicker, { color: headerColors.color }]}>
          CARNET FICHES RESSOURCES
        </Text>
      </View>
      <View style={styles.headerCenter}>
        <Text style={[styles.headerTitle, { color: headerColors.color }]}>
          Aucune fiche ressource
        </Text>
        <Text style={[styles.headerSubtitle, { color: headerColors.color }]}>
          Reporting opérationnel chantier
        </Text>
      </View>
    </View>

    <View style={styles.emptyBody}>
      <Text style={styles.emptyTitle}>Aucune ressource à exporter</Text>
      <Text style={styles.emptyText}>
        Le carnet ne contient que les ressources de chantier actives. Vérifiez
        la sélection ou le profil de suivi du projet.
      </Text>
    </View>

    <View style={styles.footer}>
      <Text style={styles.footerText}>Reporting opérationnel chantier</Text>
      {date ? (
        <Text style={styles.footerText}>
          Version du {formatDateLabel(date, locale)}
        </Text>
      ) : null}
    </View>
  </Page>
)

export const OperationalResourceSheetsDocument = ({
  products,
  context,
  date,
  locale = 'fr',
}: OperationalResourceSheetsDocumentProps) => {
  const sheets = buildOperationalResourceSheets(products, {
    outletOptionsByID: context.optionsByID?.target_outlet,
  })
  const documentLabel = getDocumentLabel(products, context)
  const headerColors = resolveOperationalResourceSheetsHeaderColors(context)

  return (
    <Document
      title={`Carnet fiches ressources ${documentLabel}`.trim()}
      subject="Reporting opérationnel chantier"
      author="Skop"
      producer="Skop"
      creator="Skop"
      language={locale}
    >
      {sheets.length ? (
        sheets.map((sheet) => (
          <ResourceSheetPage
            key={sheet.id}
            sheet={sheet}
            headerColors={headerColors}
            date={date}
            locale={locale}
          />
        ))
      ) : (
        <EmptyResourceSheetsPage
          headerColors={headerColors}
          date={date}
          locale={locale}
        />
      )}
    </Document>
  )
}

const styles = StyleSheet.create({
  page: {
    padding: '0.85cm',
    backgroundColor: '#ffffff',
    fontFamily: 'InterOperationalReporting',
    color: '#173f3d',
  },
  header: {
    flexDirection: 'row',
    minHeight: '1.8cm',
    backgroundColor: '#173f3d',
    color: '#ffffff',
  },
  headerCell: {
    width: '31%',
    justifyContent: 'center',
    paddingHorizontal: 10,
    borderRight: '0.75 solid #ffffff',
  },
  headerCenter: {
    width: '69%',
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 10,
  },
  headerKicker: {
    fontSize: 8,
    textTransform: 'uppercase',
    fontWeight: 'bold',
  },
  headerTitle: {
    fontSize: 10,
    fontWeight: 'bold',
    textAlign: 'center',
    textTransform: 'uppercase',
  },
  headerSubtitle: {
    marginTop: 4,
    fontSize: 8,
    textAlign: 'center',
  },
  body: {
    flexDirection: 'row',
    paddingTop: 8,
  },
  pictureColumn: {
    width: '31%',
    paddingRight: 8,
  },
  picture: {
    width: '100%',
    height: '8.3cm',
    objectFit: 'cover',
    marginBottom: 8,
    border: '0.75 solid #d6e0df',
  },
  picturePlaceholder: {
    width: '100%',
    height: '8.3cm',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#f1f5f4',
    border: '0.75 solid #d6e0df',
  },
  picturePlaceholderText: {
    fontSize: 9,
    color: '#6b7c79',
  },
  contentColumn: {
    width: '69%',
  },
  emptyBody: {
    marginTop: 16,
    padding: 12,
    border: '0.75 solid #d6e0df',
    backgroundColor: '#f8fbfa',
  },
  emptyTitle: {
    fontSize: 11,
    fontWeight: 'bold',
    marginBottom: 6,
  },
  emptyText: {
    fontSize: 9,
    lineHeight: 1.35,
    color: '#425653',
  },
  section: {
    borderTop: '0.75 solid #173f3d',
    paddingTop: 5,
    marginBottom: 7,
  },
  sectionTitle: {
    fontSize: 8,
    fontWeight: 'bold',
    textTransform: 'uppercase',
    marginBottom: 4,
  },
  tableRow: {
    flexDirection: 'row',
    paddingVertical: 2,
  },
  tableLabel: {
    width: '34%',
    fontSize: 7,
    fontWeight: 'bold',
    color: '#173f3d',
  },
  tableValue: {
    width: '66%',
    fontSize: 7,
    color: '#243b39',
  },
  footer: {
    position: 'absolute',
    left: '0.85cm',
    right: '0.85cm',
    bottom: '0.45cm',
    flexDirection: 'row',
    justifyContent: 'space-between',
    borderTop: '0.75 solid #d6e0df',
    paddingTop: 4,
  },
  footerText: {
    fontSize: 7,
    color: '#6b7c79',
  },
})
